import { TamediaStyles } from "@ta-interaktiv/microfrontend-template-helpers";
import { FC, ReactElement, useEffect, useState } from 'react'

import styled from 'styled-components'
import colorVariables from '../../Globals/colors.module.css'
import fontVariables from '../../Globals/fonts.module.css'
import { executeWhenReady, MapBoxMap, toggleLayers } from 'components/MapBoxMap'
import { ContentWidth } from 'components/Container'
import mapboxgl, { LngLat } from 'mapbox-gl'
import { PopUp } from 'components/PopUp'
import { Scroller } from 'components/Scroller'
import { Key } from 'components/Chart_Key'
import { useMediaQuery, useWindowSize } from 'usehooks-ts'
import { Marker } from 'components/Marker'
import { frontElements } from './content'
import { InteractiveToggle } from 'components/InteractiveToggle'
import { ImageMarker } from 'components/ImageMarker'
const clone = (e: any) => JSON.parse(JSON.stringify(e))

const lightLayers = ['light', 'light-outline']
const heavyLayers = ['heavy', 'heavy-outline']
const deathLayers = ['death', 'death-outline']
const lightHurtLayers = ['hurt-light-outline', 'hurt-light']

const allAccidentLayers = [...lightLayers, ...deathLayers, ...heavyLayers]
const windowheight = window.innerHeight
// const toggleCursorToPoi = (map: mapboxgl.Map) => {
//   map.getCanvas().style.cursor = 'pointer'
// }
// const toggleCursorToDef = (map: mapboxgl.Map) => {
//   map.getCanvas().style.cursor = 'default'
// }

const DietikonBounds = [
  [8.29279, 47.36164],
  [8.575003, 47.450078],
]
const DomatEmsBounds = [
  [9.3843704407, 46.801937412],
  [9.5496870103, 46.8751666537],
]
const SwitzerlandBoundsMobile = [
  [5.6, 42],
  [10.8, 51],
]
const SwitzerlandBoundsDesktop = [
  [4, 44],
  [12, 50],
]
const SwitzerlandViewBox = [
  [5.6, 45],
  [10.9, 48],
]
const SwitzerlandViewBoxMobile = [
  [5.5, 45.8],
  [10.5, 47.8],
]
const MapActions = {
  _0: {
    // DIETIKON
    viewBounds: clone(DietikonBounds),
    viewBounds_mobile: clone(DietikonBounds),
    changes: (map: mapboxgl.Map) => {
      toggleLayers(map, {
        ids: [...allAccidentLayers, ...lightHurtLayers],
        visible: false,
      })
    },
  },
  _1: {
    // NUR VERLETZTE GANZE SCHWEIZ
    viewBounds: clone(SwitzerlandViewBox),
    viewBounds_mobile: clone(SwitzerlandViewBoxMobile),
    changes: (map: mapboxgl.Map) => {
      toggleLayers(map, {
        ids: [...allAccidentLayers, ...lightHurtLayers],
        visible: false,
      })
      toggleLayers(map, {
        ids: [...heavyLayers, ...lightHurtLayers],
        visible: true,
      })
    },
  },
  _2: {
    // NUR VERLETZTE GANZE SCHWEIZ
    viewBounds: clone(SwitzerlandViewBox),
    viewBounds_mobile: clone(SwitzerlandViewBoxMobile),
    changes: (map: mapboxgl.Map) => {
      toggleLayers(map, {
        ids: [...heavyLayers, ...lightHurtLayers],
        visible: true,
      })
      toggleLayers(map, {
        ids: [...lightLayers, ...deathLayers],
        visible: false,
      })
    },
  },
  _3: {
    // Domat/Ems
    viewBounds: clone(DomatEmsBounds),
    viewBounds_mobile: clone(DomatEmsBounds),
    changes: (map: mapboxgl.Map) => {
      toggleLayers(map, {
        ids: [...allAccidentLayers, ...lightHurtLayers],
        visible: false,
      })
    },
  },
  _4: {
    viewBounds: clone(SwitzerlandViewBox),
    viewBounds_mobile: clone(SwitzerlandViewBoxMobile),
    changes: (map: mapboxgl.Map) => {
      toggleLayers(map, {
        ids: [...lightLayers, ...heavyLayers, ...lightHurtLayers],
        visible: false,
      })
      toggleLayers(map, {
        ids: deathLayers,
        visible: true,
      })
    },
  },
  _5: {
    viewBounds: clone(SwitzerlandViewBox),
    viewBounds_mobile: clone(SwitzerlandViewBoxMobile),
    changes: (map: mapboxgl.Map) => {
      toggleLayers(map, {
        ids: [...lightLayers, ...heavyLayers, ...lightHurtLayers],
        visible: false,
      })
      toggleLayers(map, {
        ids: deathLayers,
        visible: true,
      })
    },
  },
  _6: {
    viewBounds: clone(SwitzerlandViewBox),
    viewBounds_mobile: clone(SwitzerlandViewBoxMobile),
    changes: (map: mapboxgl.Map) => {
      toggleLayers(map, { ids: lightHurtLayers, visible: false })
      toggleLayers(map, {
        ids: allAccidentLayers,
        visible: true,
      })
    },
  },
  _7: {
    viewBounds: clone(SwitzerlandViewBox),
    viewBounds_mobile: clone(SwitzerlandViewBoxMobile),
    changes: (map: mapboxgl.Map) => {
      toggleLayers(map, { ids: lightHurtLayers, visible: false })
      toggleLayers(map, {
        ids: allAccidentLayers,
        visible: true,
      })
    },
  },
}

const MapContainer = ({
  width,
  children,
}: {
  width: number
  children: JSX.Element[] | JSX.Element | string
}) => (width < 712 ? <>{children}</> : <ContentWidth>{children}</ContentWidth>)

const MainComponent: FC<MicroFrontendProps> = ({
  tenant,
  colorMode,
}): ReactElement => {
  const globalClasses = [
    colorVariables.globalColors,
    fontVariables.globalFonts,
    colorMode === 'dark' ? colorVariables.globalColorsDark : '',
  ]

  const [size, setSize] = useState<{ width: number; height: number }>({
    width: window.innerWidth,
    height: window.innerHeight,
  })
  const [map, setMap] = useState<mapboxgl.Map>()
  const [popupcontent, setPopupcontent] = useState<
    | {
        pos: { x: number; y: number }
        data: { [k: string]: any }
        coors: mapboxgl.LngLat
        anchor: string
      }
    | undefined
  >()
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  const { width } = useWindowSize()

  useEffect(() => {
    setSize({ width: window.innerWidth, height: windowheight })
  }, [])
  useEffect(() => {
    if (!map) return
  }, [map])

  useEffect(() => {
    setSize({ width: width, height: windowheight })
  }, [width])

  useEffect(() => {
    if (!map) return
    map.setStyle(
      colorMode === 'light'
        ? 'mapbox://styles/ta-interaktiv/cl5975oco007e14pnqykvjx1o'
        : 'mapbox://styles/ta-interaktiv/cl617quah002314mrldv6rhv1'
    )
  }, [colorMode, map])

  return (
    <AppContainer className={globalClasses.join(' ') + ' ' + colorMode}>
      <ColorWrapper colorMode={colorMode}>
        <Scroller
          defaultDistanceTop={
            size.width < 712 ? size.height * 0.8 : size.height * 0.8
          }
          defaultDistanceBottom={
            size.width < 712 ? size.height * 0.8 : size.height * 0.8
          }
          front={frontElements(size, colorMode)}
          back={(index, id) => {
            // console.log(index, id)

            const clickHandler = (e: mapboxgl.MapMouseEvent) => {
              let clickedpoint = map
                .queryRenderedFeatures(e.point)
                .filter((f) => f.sourceLayer === 'map_12_07_2022-camopx')
              if (
                clickedpoint[0] &&
                clickedpoint[0].properties &&
                clickedpoint[0].properties.Datum
              ) {
                const pos = e.point
                const coors = clickedpoint[0].geometry['coordinates']

                setPopupcontent({
                  pos,
                  coors: { lng: coors[0], lat: coors[1] } as LngLat,
                  data: clickedpoint[0].properties,
                  anchor:
                    (pos.y > size.height * 0.5 ? 'bottom' : 'top') +
                    '-' +
                    (pos.x > size.width * 0.5 ? 'right' : 'left'),
                })
              } else {
                setPopupcontent(undefined)
              }
            }
            const moveHandler = (e: mapboxgl.MapMouseEvent) => {
              if (popupcontent && popupcontent.data) {
                const pos = map.project(popupcontent.coors)

                setPopupcontent({
                  ...popupcontent,
                  pos,
                  anchor:
                    (pos.y > size.height * 0.5 ? 'bottom' : 'top') +
                    '-' +
                    (pos.x > size.width * 0.5 ? 'right' : 'left'),
                })
              } else {
                setPopupcontent(undefined)
              }
            }

            if (map) {
              if (index === 6) {
                map.on('click', clickHandler)
              } else {
                map.on('click', () => {})
              }
              if (index === 6) {
                map.on('move', moveHandler)
              } else {
                map.on('move', () => {})
              }
            }

            if (map && map.loaded) {
              // DO ALL MAP CHANGES FOR CURRENT FRONT ELEMENT
              executeWhenReady(map, () => {
                MapActions[id] && MapActions[id].changes(map)
              })
            }
            const useId =
              index >= 0 && index <= frontElements(size, colorMode).length - 1
                ? id
                : index > 0
                ? '_2'
                : '_0'

            return (
              <>
                <div
                  style={{
                    width: '100%',
                    marginBottom: width < 712 ? '20%' : '10%',
                  }}
                />
                <MapContainer width={width}>
                  <MapBoxMap
                    config={{
                      style:
                        colorMode === 'light'
                          ? 'mapbox://styles/ta-interaktiv/cl5975oco007e14pnqykvjx1o'
                          : 'mapbox://styles/ta-interaktiv/cl617quah002314mrldv6rhv1',
                    }}
                    dataSource={'BFS'}
                    animationDuration={reducedMotion ? 1 : 800}
                    viewBounds={
                      !MapActions[useId]
                        ? undefined
                        : width < 712
                        ? MapActions[useId].viewBounds_mobile
                        : MapActions[useId].viewBounds
                    }
                    maxExtendBounds={
                      width < 712
                        ? clone(SwitzerlandBoundsMobile)
                        : clone(SwitzerlandBoundsDesktop)
                    }
                    showInteractionToggle={index > 5}
                    interActionToggle={InteractiveToggle}
                    handleInteractivityChange={() => {
                      setPopupcontent(undefined)
                    }}
                    init={(map: mapboxgl.Map) => {
                      setMap(map)
                    }}
                  >
                    {map && popupcontent && popupcontent.coors && (
                      <PopUp
                        anchor={popupcontent.anchor}
                        pos={map.project(popupcontent.coors)}
                        data={popupcontent.data}
                      />
                    )}
                    {map && index <= 0 && (
                      <Marker map={map} latlng={[8.423778, 47.396528]}>
                        <ImageMarker
                          src={require('../../Assets/kapo_frontallkollision_dietikon.jpg')}
                          title='Schwerer Unfall in Dietikon'
                          caption='Hier kollidierte der BMW mit dem Ford der Familie B.'
                          source='Foto: Kapo ZH'
                          colorMode={colorMode}
                        />
                      </Marker>
                    )}
                    {map && index === 3 && (
                      <Marker map={map} latlng={[9.474667, 46.839694]}>
                        <ImageMarker
                          src={require('../../Assets/domatems_2.jpg')}
                          title='Tödlicher Unfall in Domat/Ems'
                          source='Foto: Kapo GR'
                          colorMode={colorMode}
                        />
                      </Marker>
                    )}
                    <div
                      className='wrapper'
                      style={{
                        zIndex: 2100,
                        position: 'absolute',
                        bottom: '-5px',
                        width: '100%',
                      }}
                    >
                      {index === 6 && (
                        <Key
                          swatchSize={22}
                          style={{
                            backgroundColor: 'var(--site-background)',
                            padding: '30px',
                            paddingTop: '0px',
                            paddingBottom: '5px',
                          }}
                          entries={[
                            {
                              label:
                                'Unfälle in Verbindung mit Geschwindigkeitsübertretungen',

                              entries: [
                                {
                                  type: 'circle',
                                  label: 'maximal Leichtverletzte',
                                  color: '#FFE462',
                                },
                                {
                                  type: 'circle',
                                  label: 'mit Schwerverletzten',
                                  color: '#E78D38',
                                },
                                {
                                  type: 'circle',
                                  label: 'mit Verstorbenen',
                                  color: '#D3343F',
                                },
                              ],
                            },
                          ]}
                        />
                      )}
                    </div>
                  </MapBoxMap>
                </MapContainer>
              </>
            )
          }}
        />
      </ColorWrapper>
    </AppContainer>
  )
}

const ColorWrapper = styled.div<{ colorMode: 'light' | 'dark' }>`
  p {
    color: ${({ colorMode }) =>
      colorMode === 'light' ? '#202346' : '#E9E9E9'};
  }
`

const AppContainer = styled(TamediaStyles)<{ tenant?: string }>`
  width: 100%;
  margin: 0;
  padding: 0;
  font-family: var(--font-sans-serif);
  color: var(--brandblue-main);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  code {
    font-family: source-code-pro, Menlo, Monaco, Consolas, 'Courier New',
      monospace;
  }
  .App-logo {
    height: 40vmin;
    pointer-events: none;
  }

  @media (prefers-reduced-motion: no-preference) {
    .App-logo {
      animation: App-logo-spin infinite 20s linear;
    }
  }

  .App-header {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-size: 3rem;
    .tenant {
      ${(props) => (props.tenant ? `color: var(--tenant-${props.tenant})` : '')}
    }
  }

  @keyframes App-logo-spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`

export default MainComponent
